/**
 * Feature-flag + capability registry shared by the tenant app and the API.
 *
 * Three independent gates compose behind one capability: an optional PostHog feature `flag` (rollout), a CASL
 * `permission` (who), and a billing `entitlement` (plan). Today only the flag is wired; `permission` and
 * `entitlement` are null stubs so later phases can fill them in without touching the ~call sites.
 */

/** Canonical PostHog flag keys. Keep in sync with the flags created in the PostHog project. */
export const FeatureFlag = {
  ReportsBeta: "reports-beta",
  RafikiAssistant: "rafiki-assistant",
  RafikiVoice: "rafiki-voice",
  Exports: "exports",
  PublicForms: "public-forms",
  PushNotifications: "push-notifications",
  DepartmentFinance: "department-finance",
  /** Budgets & stewardship (IBA-157). Released — kept as a kill switch, no longer a release gate. */
  Budgeting: "budgeting",
  /** Asset stewardship. Released — kept as a kill switch, no longer a release gate. */
  Assets: "assets",
  /** Work queue (approvals and tasks). Released — kept as a kill switch, no longer a release gate. */
  WorkQueue: "work-queue",

  /* ---------------------------------------------------------------------- */
  /* PAUSED FOR V1                                                           */
  /*                                                                        */
  /* The first release is deliberately six features: Members, Groups &       */
  /* Ministries, Activities, Finance/Accounting, Workspace Administration,   */
  /* and Announcements & Notifications. Everything below is built (or partly */
  /* built) but is NOT part of that release, so it ships dark: the flag      */
  /* exists, defaults OFF, and hides both the nav entry and the route.       */
  /*                                                                        */
  /* Paused, not deleted. Turning any of these on is a PostHog toggle, not a */
  /* code change — which is the whole point of registering them here rather  */
  /* than commenting the features out.                                       */
  /* ---------------------------------------------------------------------- */
  Pledges: "pledges",
  /** Subscription/pricing UI. There is NO billing backend yet — this must not be reachable. */
  Billing: "billing",
} as const;

export type FeatureFlagKey = (typeof FeatureFlag)[keyof typeof FeatureFlag];

/**
 * Fail-safe value used when PostHog is unavailable (local dev, SDK not loaded, eval error). Release/beta
 * flags default OFF (don't expose unfinished features); kill switches default ON (an outage must not
 * disable a live feature, since the kill is a deliberate action).
 */
export const FEATURE_FLAG_FALLBACK: Record<FeatureFlagKey, boolean> = {
  [FeatureFlag.ReportsBeta]: false,
  [FeatureFlag.RafikiVoice]: false,
  [FeatureFlag.DepartmentFinance]: false,

  // Cross-cutting capabilities the V1 features depend on. Exports back the finance reports a treasurer needs;
  // push backs Notifications. Both stay ON — they are kill switches, not release gates.
  [FeatureFlag.Exports]: true,
  [FeatureFlag.PushNotifications]: true,

  // Budgeting is RELEASED (IBA-157). It is now a kill switch like the two above: ON by default, so a PostHog
  // outage cannot silently hide a live feature — and turning it off stays a deliberate act. Every consumer
  // already reads the flag (widget registry, quick actions, global create, the transaction line picker), so
  // this one value is the whole switch.
  [FeatureFlag.Budgeting]: true,
  [FeatureFlag.Assets]: true,
  // Work Queue is released and enabled at 100% in PostHog, so it is a kill switch too: ON here, and turning
  // it off stays a deliberate act rather than something a PostHog outage can do.
  [FeatureFlag.WorkQueue]: true,

  /* --- PAUSED FOR V1 (see the note on FeatureFlag above) --- */

  // Built and working, but not in the first release. OFF by default, so a PostHog outage cannot accidentally
  // reveal them — for a paused feature the fail-safe direction is HIDDEN, the opposite of a kill switch.
  [FeatureFlag.Pledges]: false,

  // Rafiki was ON as a kill switch. It is now paused: an AI assistant answering questions about numbers the
  // church does not trust yet is backwards, and it invites "why is this wrong?" before we have earned it.
  [FeatureFlag.RafikiAssistant]: false,

  // Public intake is LIVE, and `public-forms` is now a kill switch in PostHog ("Global, on by default; flip
  // off to disable public form endpoints"), not the release gate it was when Smart Forms was still paused.
  // So it follows the kill-switch rule above: ON.
  //
  // As `false` it was the opposite of fail-safe. The API only reaches this fallback when it CANNOT evaluate
  // the flag — no PostHog key, or PostHog unreachable — and in exactly that situation it took a public form
  // that every visitor can open and refused its submissions with "Public form submissions are currently
  // disabled", while PostHog showed the flag enabled at 100%. An outage must not perform the kill.
  [FeatureFlag.PublicForms]: true,

  // There is NO billing backend (no Stripe, no billing module). The subscription/pricing screens are a facade,
  // and a facade that takes payment details is worse than no screen at all.
  [FeatureFlag.Billing]: false,
};

/** CASL-style permission requirement for a capability. Null = no permission gate. Filled in a later phase. */
export type CapabilityPermission = { module: string; level: "VIEW" | "EDIT" } | null;
/** Billing entitlement key required for a capability. Null = not plan-gated. Filled when billing lands. */
export type CapabilityEntitlement = string | null;

export type CapabilityDefinition = {
  flag: FeatureFlagKey | null;
  permission: CapabilityPermission;
  entitlement: CapabilityEntitlement;
};

/** Helper so each entry is typed as `CapabilityDefinition` (permission/entitlement stay extensible, not literal null). */
const defineCapability = (
  flag: FeatureFlagKey | null,
  permission: CapabilityPermission = null,
  entitlement: CapabilityEntitlement = null,
): CapabilityDefinition => ({ flag, permission, entitlement });

/**
 * The capability registry. Call sites ask for a capability (e.g. "reports.view"), never a raw flag, so a
 * later phase can add a `permission`/`entitlement` to a capability and every call site inherits the new
 * check with no edits.
 */
export const CAPABILITIES = {
  "reports.view": defineCapability(FeatureFlag.ReportsBeta),
  // Forms is released and has no tenant-module rollout flag. `public-forms` is a separate server-side
  // kill switch for anonymous intake and must never decide whether authenticated users can see Forms.
  "forms.view": defineCapability(null),
  "rafiki.use": defineCapability(FeatureFlag.RafikiAssistant),
  "rafiki.voice": defineCapability(FeatureFlag.RafikiVoice),
  "exports.run": defineCapability(FeatureFlag.Exports),
  "publicForms.intake": defineCapability(FeatureFlag.PublicForms),
  "push.enable": defineCapability(FeatureFlag.PushNotifications),
  "departmentFinance.view": defineCapability(FeatureFlag.DepartmentFinance),

  /* --- paused for V1 --- */
  "budgeting.view": defineCapability(FeatureFlag.Budgeting),
  "pledges.view": defineCapability(FeatureFlag.Pledges),
  "assets.view": defineCapability(FeatureFlag.Assets),
  "workQueue.view": defineCapability(FeatureFlag.WorkQueue),
  "billing.manage": defineCapability(FeatureFlag.Billing),
};

export type Capability = keyof typeof CAPABILITIES;

/** Which gate failed, so the UI can pick the right affordance (hide vs ask-admin vs upgrade-CTA). */
export type FeatureAccessReason = "flag" | "permission" | "plan" | null;
export type FeatureAccessResult = {
  allowed: boolean;
  reason: FeatureAccessReason;
  /**
   * Diagnostics for a `flag` denial — SERVER-SIDE ONLY, never part of an API response.
   *
   * Which flag refused, and whether the refusal came from PostHog or from a value compiled into the build.
   * Both denials are the same 403 to a caller, and only one of them is a decision someone took: with no
   * PostHog key on a service, every flag silently answers from its fail-safe while the dashboard shows it
   * enabled. These two fields are what let a log line say so.
   */
  flag?: string;
  source?: string;
};
